import React, { useState } from 'react'
import SeoHead from '../components/SeoHead'
import GallerySection from '../components/GallerySection'
import { HanddrawnArrow } from '../components/HanddrawnArrow'
import { ArcOrbitButton } from '../components/ArcOrbitButton'

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: 'Spørgsmål til optagelse eller skoleophold',
    message: '',
    website_url: '', // Honeypot
  })

  const [status, setStatus] = useState({ loading: false, success: false, error: null })

  const handleSubmit = async (e) => {
    e.preventDefault()
    setStatus({ loading: true, success: false, error: null })

    try {
      const res = await fetch('/_api/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim(),
          subject: formData.subject.trim(),
          message: formData.message.trim(),
          website_url: formData.website_url, // Honeypot
        }),
      })

      const data = await res.json().catch(() => ({}))

      if (res.ok) {
        setStatus({ loading: false, success: true, error: null })
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: 'Spørgsmål til optagelse eller skoleophold',
          message: '',
          website_url: '',
        })
      } else {
        setStatus({
          loading: false,
          success: false,
          error: data.message || 'Der opstod en fejl under afsendelsen. Prøv venligst igen eller ring til os.',
        })
      }
    } catch (err) {
      setStatus({
        loading: false,
        success: false,
        error: 'Kunne ikke oprette forbindelse. Tjek din internetforbindelse eller ring direkte til 74 72 44 33.',
      })
    }
  }

  return (
    <div className="w-full bg-white">
      <SeoHead
        title="Kontakt os - Emmerske Efterskole"
        description="Kontakt Emmerske Efterskole i Tønder. Telefon: 74 72 44 33, E-mail: ee@eeskole.dk. Find vej, åbningstider og send os en besked."
      />

      {/* Underside Hero Banner */}
      <section className="relative bg-[#36454F] text-white pt-36 sm:pt-44 lg:pt-52 pb-24 sm:pb-32 lg:pb-36 min-h-[440px] sm:min-h-[500px] flex items-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="/_assets/dsc00973_1268c8a9.jpg"
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover object-center"
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(to right, rgba(54,69,79,0.85) 0%, rgba(54,69,79,0.65) 50%, rgba(47,111,115,0.45) 100%)',
            }}
          />
        </div>

        <div className="background-blob -bottom-24 -right-24 bg-white/10" aria-hidden="true" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-4 relative z-10 w-full text-center">
          <span className="font-['Shadows_Into_Light'] text-2xl sm:text-3xl md:text-4xl text-[#DCEEF2] -rotate-2 inline-block drop-shadow-sm">
            Vi sidder klar til at hjælpe
          </span>

          <h1 className="font-['Plus_Jakarta_Sans'] text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-black text-white tracking-tight leading-tight max-w-4xl mx-auto">
            Kontakt Emmerske Efterskole
          </h1>

          <p className="text-[#DCEEF2] text-base sm:text-lg leading-relaxed max-w-2xl mx-auto font-medium">
            Uanset om du har spørgsmål til ordblindestøtte, økonomi, optagelse eller en hverdag på
            skolen, sidder vi klar til at svare på dine henvendelser.
          </p>
        </div>
      </section>

      {/* Main Container */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          
          {/* Kolonne 1: Faste Kontaktoplysninger & Rutevejledning */}
          <div className="lg:col-span-5 space-y-8">
            <div className="bg-[#DCEEF2]/50 rounded-[2.5rem] p-8 sm:p-10 border border-[#DCEEF2] shadow-sm space-y-6">
              <div>
                <span className="font-['Shadows_Into_Light'] text-xl text-[#2F6F73] block mb-1">
                  Direkte kontakt
                </span>
                <h2 className="font-['Plus_Jakarta_Sans'] text-2xl font-black text-[#36454F]">
                  Officielle kontaktoplysninger
                </h2>
              </div>

              <div className="space-y-5 text-sm sm:text-base">
                {/* Adresse */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-organic bg-[#007A53] text-white flex items-center justify-center flex-shrink-0 shadow-sm">
                    <i className="fa-solid fa-location-dot text-lg" aria-hidden="true" />
                  </div>
                  <div>
                    <div className="font-['Plus_Jakarta_Sans'] font-black text-[#36454F] text-base">Emmerske Efterskole</div>
                    <div className="text-[#36454F]/80">Åbenråvej 14</div>
                    <div className="text-[#36454F]/80">6270 Tønder</div>
                    <a
                      href="https://maps.google.com/?q=%C3%85benr%C3%A5vej+14,+6270+T%C3%B8nder"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs font-bold text-[#007A53] hover:underline mt-2"
                    >
                      <i className="fa-solid fa-map-location-dot" aria-hidden="true" />
                      <span>Få rutevejledning i Google Maps</span>
                    </a>
                  </div>
                </div>

                {/* Telefon */}
                <div className="flex items-start gap-4 pt-4 border-t border-[#2F6F73]/15">
                  <div className="w-12 h-12 rounded-organic bg-[#2F6F73] text-white flex items-center justify-center flex-shrink-0 shadow-sm">
                    <i className="fa-solid fa-phone text-lg" aria-hidden="true" />
                  </div>
                  <div>
                    <div className="text-xs text-[#536363] uppercase font-bold tracking-wider">Telefon</div>
                    <a
                      href="tel:74724433"
                      className="text-xl font-['Plus_Jakarta_Sans'] font-black text-[#36454F] hover:text-[#007A53] transition-colors whitespace-nowrap block mt-0.5"
                    >
                      74 72 44 33
                    </a>
                    <span className="text-xs text-[#536363] block mt-1">
                      Mandag – torsdag: 08.00 – 15.30 · Fredag: 08.00 – 14.00
                    </span>
                  </div>
                </div>

                {/* E-mail */}
                <div className="flex items-start gap-4 pt-4 border-t border-[#2F6F73]/15">
                  <div className="w-12 h-12 rounded-organic bg-[#007A53] text-white flex items-center justify-center flex-shrink-0 shadow-sm">
                    <i className="fa-solid fa-envelope text-lg" aria-hidden="true" />
                  </div>
                  <div>
                    <div className="text-xs text-[#536363] uppercase font-bold tracking-wider">E-mail</div>
                    <a
                      href="mailto:ee@eeskole.dk"
                      className="text-base font-bold text-[#36454F] hover:text-[#007A53] transition-colors whitespace-nowrap block mt-0.5"
                    >
                      ee@eeskole.dk
                    </a>
                    <span className="text-xs text-[#536363] block mt-1">
                      Vi svarer hurtigst muligt inden for 24 timer på hverdage.
                    </span>
                  </div>
                </div>

                {/* CVR og EAN */}
                <div className="pt-4 border-t border-[#2F6F73]/15 flex items-center justify-between text-xs text-[#536363] font-bold">
                  <span>CVR: 83992611</span>
                  <span>EAN: 579 000 249 8367</span>
                </div>
              </div>
            </div>

            {/* Quick Box */}
            <div className="bg-[#2F6F73] text-white rounded-[2.5rem] p-7 sm:p-8 shadow-lg space-y-4">
              <span className="font-['Shadows_Into_Light'] text-2xl text-[#DCEEF2] block leading-none">
                Personlig oplevelse
              </span>
              <h3 className="font-['Plus_Jakarta_Sans'] font-black text-xl leading-snug">
                Vil du opleve skolen ved selvsyn?
              </h3>
              <p className="text-xs sm:text-sm leading-relaxed text-white/90">
                Hvis du overvejer et efterskoleophold, er en personlig rundvisning den bedste start.
                Du kan booke tid direkte i vores kalender online.
              </p>
              <div className="pt-2">
                <ArcOrbitButton
                  to="/rundvisning"
                  variant="white"
                  icon={<i className="fa-solid fa-calendar-check text-[#007A53] text-base" aria-hidden="true" />}
                >
                  Book rundvisning online
                </ArcOrbitButton>
              </div>
            </div>
          </div>

          {/* Kolonne 2: Kontaktformular */}
          <div className="lg:col-span-7">
            <div className="bg-white rounded-[2.5rem] p-8 sm:p-12 border border-[#DCEEF2] shadow-xl">
              <span className="font-['Shadows_Into_Light'] text-2xl text-[#007A53] block mb-1">
                Skriv til os
              </span>
              <h2 className="font-['Plus_Jakarta_Sans'] text-2xl sm:text-3xl font-black text-[#36454F] mb-2 tracking-tight">
                Send os en besked
              </h2>
              <p className="text-[#36454F]/80 text-sm sm:text-base mb-8">
                Udfyld formularen, så kontakter vi dig snarest muligt.
              </p>

              {status.success ? (
                <div className="p-8 sm:p-10 rounded-3xl bg-[#DCEEF2] border border-[#007A53]/30 text-center space-y-4 animate-in fade-in">
                  <div className="w-16 h-16 rounded-organic bg-[#007A53] text-white flex items-center justify-center mx-auto text-2xl shadow-md">
                    <i className="fa-solid fa-check" aria-hidden="true" />
                  </div>
                  <h3 className="font-['Plus_Jakarta_Sans'] text-2xl font-black text-[#36454F]">
                    Tak for din henvendelse!
                  </h3>
                  <p className="text-[#36454F]/85 text-sm sm:text-base max-w-md mx-auto leading-relaxed">
                    Vi har modtaget din besked og vender tilbage hurtigst muligt. Har du akutte
                    spørgsmål, er du altid velkommen til at ringe på 74 72 44 33.
                  </p>
                  <div className="pt-2">
                    <button
                      type="button"
                      onClick={() => setStatus({ loading: false, success: false, error: null })}
                      className="btn-secondary !text-sm"
                    >
                      Send endnu en besked
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Honeypot field strictly hidden from humans */}
                  <div className="sr-only absolute -left-[9999px]" aria-hidden="true">
                    <label htmlFor="contact_website_url">Hjemmeside</label>
                    <input
                      id="contact_website_url"
                      type="text"
                      name="website_url"
                      tabIndex="-1"
                      value={formData.website_url}
                      onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                      autoComplete="off"
                    />
                  </div>

                  {status.error && (
                    <div className="p-4 rounded-2xl bg-red-50 text-red-700 text-sm border border-red-200">
                      {status.error}
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="contact_name" className="block text-xs font-bold uppercase tracking-wider text-[#36454F] mb-1.5">
                        Fulde navn <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="contact_name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="F.eks. Lene Møller"
                        className="w-full px-4 py-3.5 rounded-2xl border border-[#DCEEF2] bg-white text-[#36454F] text-sm focus:outline-none focus:ring-2 focus:ring-[#007A53] transition-all"
                      />
                    </div>

                    <div>
                      <label htmlFor="contact_phone" className="block text-xs font-bold uppercase tracking-wider text-[#36454F] mb-1.5">
                        Telefonnummer <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="contact_phone"
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="12 34 56 78"
                        className="w-full px-4 py-3.5 rounded-2xl border border-[#DCEEF2] bg-white text-[#36454F] text-sm focus:outline-none focus:ring-2 focus:ring-[#007A53] transition-all"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="contact_email" className="block text-xs font-bold uppercase tracking-wider text-[#36454F] mb-1.5">
                      E-mailadresse <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="contact_email"
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="din@adresse.dk"
                      className="w-full px-4 py-3.5 rounded-2xl border border-[#DCEEF2] bg-white text-[#36454F] text-sm focus:outline-none focus:ring-2 focus:ring-[#007A53] transition-all"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact_subject" className="block text-xs font-bold uppercase tracking-wider text-[#36454F] mb-1.5">
                      Emne
                    </label>
                    <select
                      id="contact_subject"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      className="w-full px-4 py-3.5 rounded-2xl border border-[#DCEEF2] bg-white text-[#36454F] text-sm focus:outline-none focus:ring-2 focus:ring-[#007A53] transition-all"
                    >
                      <option value="Spørgsmål til optagelse eller skoleophold">Optagelse & ledige pladser</option>
                      <option value="Ordblindestøtte og LST-værktøjer">Ordblindestøtte & IT-hjælpemidler</option>
                      <option value="Økonomi og skolepenge">Økonomi & tilskud</option>
                      <option value="Praktiske valgfag">Valgfag & værksteder</option>
                      <option value="Andet">Andet spørgsmål</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="contact_message" className="block text-xs font-bold uppercase tracking-wider text-[#36454F] mb-1.5">
                      Din besked <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="contact_message"
                      rows="5"
                      required
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Skriv dit spørgsmål her..."
                      className="w-full px-4 py-3.5 rounded-2xl border border-[#DCEEF2] bg-white text-[#36454F] text-sm focus:outline-none focus:ring-2 focus:ring-[#007A53] transition-all"
                    />
                  </div>

                  <div>
                    <button
                      type="submit"
                      disabled={status.loading}
                      className="organic-btn w-full sm:w-auto !py-4 !px-8 text-base shadow-md cursor-pointer"
                    >
                      {status.loading ? (
                        <>
                          <i className="fa-solid fa-circle-notch fa-spin mr-2" aria-hidden="true" />
                          <span>Sender besked...</span>
                        </>
                      ) : (
                        <>
                          <i className="fa-solid fa-paper-plane mr-2" aria-hidden="true" />
                          <span>Send besked</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>

        </div>
      </main>

      {/* Galleri nederst */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 sm:pb-24 w-full">
        <GallerySection
          galleryRef="faellesskab"
          customTitle="Livet på Emmerske Efterskole"
          customDescription="Et kig indenfor i hverdagen, fællesskabet og vores trygge rammer i Tønder."
        />
      </section>
    </div>
  )
}
